
import Vue from "vue";
import _ from "lodash";
import { formatDatepickerDate } from "@/shared/utils/common_utils";
import LabeledItem from "@/shared/components/LabeledItem.vue";
import AppError from "@/shared/components/AppError.vue";
import SupplierEventProduct, { INCOTerms } from "@/features/SupplierEvents/SupplierEventProduct";
import SupplierProduct, { ExtendedSupplierProduct } from "@/features/SupplierEvents/SupplierProduct";
import Country, { ExtendedCountry } from "@/features/SupplierEvents/Country";

export default Vue.extend({
    name: "SupplierEventProductModal",
    components: {
        AppError,
        LabeledItem,
    },
    data: () => ({
        dialog: true,
        // vue-form rules are eagerly evaluated as soon as an input field is selected and deselected, and displays the error messages defined in the rules.
        // We want to match the existing QRev patterns and only show errors on submission, so we start with the rules null, and add the rules on submission to display
        // error messages to the user
        rules: {
            atLeastOneProductSelected: (value: ExtendedSupplierProduct): any => {
                return value != null || "Please select a product";
            },
        },
        negotiatedItems: [
            { id: true, text: "Yes" },
            { id: false, text: "No" },
        ] as { id: boolean; text: "Yes" | "No" }[],
        errorMessage: "",
        showError: false,
        requestInProgress: false,
        searchTimeoutID: 0,
        currentINCOTermsQuery: "",
        currentINCOCountryQuery: "",
        newProposedINCOTermsQuery: "",
        newProposedINCOCountryQuery: "",
        newFinalINCOTermsQuery: "",
        newFinalINCOCountryQuery: "",
        negotiatedQuery: "",
        productQuery: "",
        product: new SupplierEventProduct(),
        selectedSupplierProduct: new ExtendedSupplierProduct(new SupplierProduct()),
        extendedSupplierProducts: [] as ExtendedSupplierProduct[],
        selectedNegotiatedItem: null as "Yes" | "No" | null,
        selectedCurrentCountry: null as ExtendedCountry | null,
        selectedProposedCountry: null as ExtendedCountry | null,
        selectedFinalCountry: null as ExtendedCountry | null,
        extendedCountries: [] as ExtendedCountry[],
        selectedStartDate: null as null | string,
        selectedCurrentINCOTerm: null as null | string,
        selectedProposedINCOTerm: null as null | string,
        selectedFinalINCOTerm: null as null | string,
        dateMenu: false,
        dateValue: null,
    }),
    props: {
        countries: {
            required: true,
            default: [] as Country[],
        },
        supplierEventProduct: {
            required: false,
            default: null as SupplierEventProduct | null,
        },
        supplierProducts: {
            required: true,
            default: [] as SupplierProduct[],
        },
        newProduct: {
            type: Boolean,
            required: true,
        },
        editMode: {
            type: Boolean,
            required: true,
        },
        priceEvent: {
            type: Boolean,
            required: true,
        },
        loading: {
            type: Boolean,
            required: true,
        },
    },
    mounted: function () {
        this.setLocalValues();
    },
    watch: {
        supplierEventProduct() {
            this.setLocalValues();
        },
        supplierProducts() {
            this.setSupplierProducts();
        },
        countries() {
            this.setLocalValuesCountries();
        },
    },
    methods: {
        setLocalValues() {
            this.product = this.supplierEventProduct ? _.cloneDeep(this.supplierEventProduct) : new SupplierEventProduct();

            this.setSupplierProducts();

            this.selectedNegotiatedItem = null;
            if (this.supplierEventProduct?.wasNegotiated != null) {
                this.selectedNegotiatedItem = this.negotiatedItems.find(ni => ni.id === this.supplierEventProduct.wasNegotiated)!.text;
            }

            this.setLocalValuesCountries();

            this.selectedStartDate = null;
            if (this.supplierEventProduct?.startDate != null) {
                this.selectedStartDate = this.supplierEventProduct?.startDate?.substring(0, 10);
            }
            this.selectedCurrentINCOTerm = null;
            if (this.supplierEventProduct?.currentINCOTerms != null) {
                this.selectedCurrentINCOTerm = this.getINCOTermString(this.supplierEventProduct.currentINCOTerms);
            }
            this.selectedProposedINCOTerm = null;
            if (this.supplierEventProduct?.newProposedINCOTerms != null) {
                this.selectedProposedINCOTerm = this.getINCOTermString(this.supplierEventProduct.newProposedINCOTerms);
            }
            this.selectedFinalINCOTerm = null;
            if (this.supplierEventProduct?.newFinalINCOTerms != null) {
                this.selectedFinalINCOTerm = this.getINCOTermString(this.supplierEventProduct.newFinalINCOTerms);
            }
        },
        setSupplierProducts() {
            this.extendedSupplierProducts = this.supplierProducts.map(sp => new ExtendedSupplierProduct(sp));

            this.selectedSupplierProduct = new ExtendedSupplierProduct(new SupplierProduct());
            if (this.supplierEventProduct != null && this.extendedSupplierProducts.findIndex(sp => sp.id == this.supplierEventProduct.productID) !== -1) {
                this.selectedSupplierProduct = this.extendedSupplierProducts.find(sp => sp.id == this.supplierEventProduct.productID)!;
                this.productQuery = this.selectedSupplierProduct.fullName;
            }
        },
        setLocalValuesCountries() {
            this.extendedCountries = this.countries.map(c => new ExtendedCountry(c));

            this.selectedCurrentCountry = null;
            this.currentINCOCountryQuery = "";
            if (this.supplierEventProduct?.currentINCOTermsCountryID != null) {
                this.selectedCurrentCountry = this.extendedCountries.find(c => c.id === this.supplierEventProduct.currentINCOTermsCountryID)!;
                this.currentINCOCountryQuery = this.selectedCurrentCountry.fullName;
            }
            this.selectedProposedCountry = null;
            this.newProposedINCOCountryQuery = "";
            if (this.supplierEventProduct?.newProposedINCOTermsCountryID != null) {
                this.selectedProposedCountry = this.extendedCountries.find(c => c.id === this.supplierEventProduct.newProposedINCOTermsCountryID)!;
                this.newProposedINCOCountryQuery = this.selectedProposedCountry.fullName;
            }
            this.selectedFinalCountry = null;
            this.newFinalINCOCountryQuery = "";
            if (this.supplierEventProduct?.newFinalINCOTermsCountryID != null) {
                this.selectedFinalCountry = this.extendedCountries.find(c => c.id === this.supplierEventProduct.newFinalINCOTermsCountryID)!;
                this.newFinalINCOCountryQuery = this.selectedFinalCountry.fullName;
            }
        },
        async saveProduct(): Promise<void> {
            this.$nextTick(async () => {
                // Manually trigger Vuetify validation
                if ((this.$refs.supplierEventProductsForm as any).validate()) {
                    //Set Product
                    this.product.productID = this.selectedSupplierProduct.id;
                    this.product.productName = this.selectedSupplierProduct.name;
                    //Set WasNegotiated
                    this.product.wasNegotiated = null;
                    if (this.selectedNegotiatedItem !== null) {
                        this.product.wasNegotiated = this.selectedNegotiatedItem == "Yes";
                    }
                    //Set INCO Terms
                    this.product.currentINCOTerms = this.selectedCurrentINCOTerm ? this.getINCOTermFromString(this.selectedCurrentINCOTerm) : null;
                    this.product.newProposedINCOTerms = this.selectedProposedINCOTerm ? this.getINCOTermFromString(this.selectedProposedINCOTerm) : null;
                    this.product.newFinalINCOTerms = this.selectedFinalINCOTerm ? this.getINCOTermFromString(this.selectedFinalINCOTerm) : null;
                    //Set Countries
                    this.product.currentINCOTermsCountryID = this.selectedCurrentCountry?.id ? this.selectedCurrentCountry?.id : null;
                    this.product.newProposedINCOTermsCountryID = this.selectedProposedCountry?.id ? this.selectedProposedCountry?.id : null;
                    this.product.newFinalINCOTermsCountryID = this.selectedFinalCountry?.id ? this.selectedFinalCountry?.id : null;
                    //Set start time
                    this.product.startDate = this.selectedStartDate ? new Date(this.selectedStartDate).toISOString() : null;

                    if (this.newProduct) {
                        this.$emit("product-added", this.product);
                    } else {
                        this.$emit("product-updated", this.product);
                    }
                }
                //if not valid, displays errors defined in the rules
            });
        },
        closeModal() {
            // Close the modal and reset values
            this.resetDialog();
            this.$emit("modal-closed");
        },
        resetDialog() {
            this.showError = false;
            this.errorMessage = "";
            this.productQuery = "";
            this.currentINCOTermsQuery = "";
            this.currentINCOCountryQuery = "";
            this.newProposedINCOTermsQuery = "";
            this.newProposedINCOCountryQuery = "";
            this.newFinalINCOTermsQuery = "";
            this.newFinalINCOCountryQuery = "";
            this.negotiatedQuery = "";
            if (this.$refs.supplierEventProductsForm != null) {
                (this.$refs.supplierEventProductsForm as any).reset();
            }
            this.dialog = false;
        },
        percentChange(oldValue: number, newValue: number): number {
            return (oldValue - newValue) / oldValue;
        },
        //Update the percent changes anytime the price inputs are changed.
        onPriceChange() {
            let proposedPercentChange = null;
            let finalPercentChange = null;
            if (this.product.newProposedPriceUSD !== null && this.product.currentPriceUSD !== null && this.product.currentPriceUSD != 0) {
                proposedPercentChange = ((this.product.newProposedPriceUSD - this.product.currentPriceUSD) * 100) / this.product.currentPriceUSD;
            }
            if (this.product.newFinalPriceUSD !== null && this.product.currentPriceUSD !== null && this.product.currentPriceUSD != 0) {
                finalPercentChange = ((this.product.newFinalPriceUSD - this.product.currentPriceUSD) * 100) / this.product.currentPriceUSD;
            }
            this.product.proposedPercentChange = proposedPercentChange;
            this.product.finalPercentChange = finalPercentChange;
        },
        formatBoolsForModal(input: boolean | null): string {
            let output: string = "";
            if (input !== null) {
                output = input ? "Yes" : "No";
            }
            return output;
        },
        formatPriceForModal(price: number | null): string {
            let output: string = "";
            if (price !== null) {
                output = "$" + price.toFixed(2);
            }
            return output;
        },
        formatPercentForModal(percent: number | null): string {
            let output: string = "";
            if (percent !== null) {
                output = percent.toFixed(2) + "%";
            }
            return output;
        },
        formatDatepickerDateForModal(dateString: string | null): string {
            let date: string = "";
            if (dateString !== null) {
                date = formatDatepickerDate(new Date(dateString));
            }
            return date;
        },
        getINCOTerms(): string[] {
            //We have to add the filter here, because of how typescript generates enums.
            //It adds the enum numeric valus as part of the keys of the Object/enum.
            return Object.keys(INCOTerms).filter(x => isNaN(parseInt(x)));
        },
        getINCOTermString(value: number): string {
            return INCOTerms[value];
        },
        getINCOTermFromString(value: string): INCOTerms {
            return INCOTerms[value as keyof typeof INCOTerms];
        },
    },
});
